import LanguageBar from "modules/localization/components/languageBar";
import React, { useEffect, useState } from "react";
import { useLanguagesData } from "../hooks/data/useLanguages";
import { PopoverOrigin } from "@mui/material";
import TranslateContentConfirmation from "./authoring/dialogs/translateContentConfirmation";
import useTranslatedContent, { TranslatableContentType, TranslatedContentType } from "../hooks/useTranslatedContent";
import { ILanguage, ILcidTranslateDetails } from "modules/localization/models";
import ConfirmDialog from "./dialogs/confirmDialog";
import { TranslationContentType } from "../models";
import LanguageBarFooter from "modules/localization/components/languageBarFooter";

interface ILanguageBarWithTranslateDialogProps<T extends TranslatableContentType> {
    translations: TranslatedContentType<T>;
    defaultContent: T;
    onChangeTranslatedContent:  (newValue: TranslatedContentType<T>) => void;
    onTranslate: (inputLanguage: string) => void;
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
    endIcon?: JSX.Element;
    calloutContentClassName?: string;
    calloutClassName?: string;
    useLanguagesData: useLanguagesData;
    requiredTranslationFieldsHasContent: (lcid: string) => boolean;
    translationHasContent: (lcid: string) => boolean;
    onOpen?: () => void;
    contentType: TranslationContentType;
    showTranslateAllOption?: boolean;
    translateAll?: (languagesToTranslate: ILcidTranslateDetails[]) => Promise<void>;
    translateAllDisabled?: boolean;
    additionalButtons?: JSX.Element;
    disableLanguageButtons?: boolean;
}

const LanguageBarWithTranslateDialog = <T extends TranslatableContentType,>({ 
    translations,
    defaultContent,
    anchorOrigin,
    transformOrigin,
    endIcon,
    calloutContentClassName,
    calloutClassName,
    useLanguagesData,
    contentType,
    translateAll = Promise.resolve,
    translateAllDisabled = true,
    showTranslateAllOption = false,
    disableLanguageButtons = false,
    additionalButtons,
    onChangeTranslatedContent,
    onTranslate,
    requiredTranslationFieldsHasContent,
    translationHasContent,
    onOpen
}: ILanguageBarWithTranslateDialogProps<T>) => {
    const [translatableLanguagesWithText, setTranslatableLanguagesWithText] = useState<ILanguage[]>([]);
    const [checkIfAnyTranslatableContent, setCheckIfAnyTranslatableContent] = useState<boolean>(false);
    const [showReplaceAllContentDialog, setShowReplaceAllContentDialog] = useState<boolean>(false);

    const {
        availableLanguages,
        selectedLanguages,
        activeLcid,
        defaultLcid,
        onRemoveLanguage,
        onSelectLanguage
    } = useLanguagesData;

    useEffect(() => {
        function setLanguageTranslationAvailability() {
            let currentAvailableTranslations = { ...translations };
            let languagesWithText: ILanguage[] = [];
            let allAddedLanguages: ILanguage[] = [];

            Object.keys(currentAvailableTranslations).forEach((nextLanguage) => {
                if (availableLanguages && translations && requiredTranslationFieldsHasContent(nextLanguage)) {
                    let matchingLang = availableLanguages.find((l) => l.lcid === nextLanguage);

                    if (matchingLang) {
                        languagesWithText.push(matchingLang);
                    }
                }
                if (availableLanguages && translations) {
                    let matchingLang = availableLanguages.find((l) => l.lcid === nextLanguage);

                    if (matchingLang) {
                        allAddedLanguages.push(matchingLang);
                    }
                }
            });

            if (languagesWithText.length >= 1 && allAddedLanguages.length > 1) {
                setCheckIfAnyTranslatableContent(false);
            } else {
                setCheckIfAnyTranslatableContent(true);
            }

            setTranslatableLanguagesWithText(languagesWithText.sort((a, b) => a.language.localeCompare(b.language)));
        }

        setLanguageTranslationAvailability();
    }, [translations, availableLanguages]);

    const {
        showTranslationDialog,
        showReplaceContentDialog,
        showRemoveLanguageDialog,
        onRemoveTranslation,
        onSelectTranslation,
        onShowTranslationDialog,
        onCloseTranslationDialog,
        onConfirmTranslationDialog,
        onConfirmRemoveLanguage,
        onConfirmReplaceContentAndTranslate,
        onCloseReplaceContentDialog,
        onCloseRemoveLanguageDialog
    } = useTranslatedContent({
        translations, 
        defaultContent,
        activeLcid,
        defaultLcid,
        autoTranslateOnAddLanguage: showTranslateAllOption, //If we're allowing translate all, we're also automatically translating.
        onChangeTranslatedContent,
        translationHasContent,
        doesLcidHaveRequiredContent: requiredTranslationFieldsHasContent,
        onTranslate,
        onSelectLanguage,
        onRemoveLanguage
    });

    const verifyAndReturnNonDefaultLcids = (): ILcidTranslateDetails[] => {
        if (!translations || !useLanguagesData.defaultLcid || !availableLanguages) return [];

        const defaultLcid = useLanguagesData.defaultLcid;
        return availableLanguages.filter((l: ILanguage) => l.lcid !== defaultLcid).map((l: ILanguage) => ({ lcid: l.lcid, translateAs: l.translateAs }));
    }

    const onTranslateAllClicked = async () => {
        const newLcids = verifyAndReturnNonDefaultLcids();
        if(newLcids.length === 0) return;

        const shouldShowReplaceContentWarning = newLcids.some(language => translationHasContent(language.lcid));

        if (shouldShowReplaceContentWarning) {
            setShowReplaceAllContentDialog(true);
        } else {
            await translateAll(newLcids);
        }
    }

    const onConfirmReplaceAllContentAndTranslate = async () => {
        const newLcids = verifyAndReturnNonDefaultLcids();
        if(newLcids.length === 0) return;
        
        setShowReplaceAllContentDialog(false);
        await translateAll(newLcids);
    }
    
    const onCloseReplaceAllContentDialog = () => {
        setShowReplaceAllContentDialog(false);
    }

    const footerContent = showTranslateAllOption
        ? <LanguageBarFooter 
            onClick={onTranslateAllClicked}
            disabled={translateAllDisabled || !requiredTranslationFieldsHasContent(defaultLcid)}
        />
        : <></>;

    return <>
        <LanguageBar
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            endIcon={endIcon}
            languages={availableLanguages?.map((lang) => ({ key: lang.lcid, value: lang.language })).sort((a, b) => a.value.localeCompare(b.value)) || []}
            activeLcid={activeLcid}
            defaultLcid={defaultLcid}
            selectedLanguages={selectedLanguages || []}
            onAddLanguage={onSelectTranslation}
            onRemoveLanguage={onRemoveTranslation}
            onSelectLanguage={onSelectTranslation}
            onTranslate={onShowTranslationDialog}
            anyAvailableTranslationsCheck={checkIfAnyTranslatableContent}
            hide={availableLanguages?.length === 1}
            calloutContentClassName={calloutContentClassName}
            calloutClassName={calloutClassName}
            onOpen={onOpen}
            footerContent={footerContent}
            disableButtons={disableLanguageButtons}
            additionalButtons={additionalButtons}
        />
        <TranslateContentConfirmation
            show={showTranslationDialog}
            onConfirm={onConfirmTranslationDialog}
            onCancel={onCloseTranslationDialog}
            currentLanguagesWithText={translatableLanguagesWithText}
            contentType={contentType}
            defaultLcid={defaultLcid}
        />
        <ConfirmDialog
            title="You'll lose your translated content"
            denyLabel="KEEP"
            confirmLabel="REMOVE"
            open={showRemoveLanguageDialog}
            onClose={onCloseRemoveLanguageDialog}
            onDeny={onCloseRemoveLanguageDialog}
            onConfirm={() => onConfirmRemoveLanguage()}
        >
            <div style={{ width: 400 }}>
                You have content on this language. Removing the language will clear the translated work you've done.
                <br />
                <br />
                Do you want to continue?
            </div>
        </ConfirmDialog>
        <ConfirmDialog
            title="Replace Content"
            confirmLabel="Yes"
            denyLabel="No"
            open={showReplaceContentDialog}
            onClose={onCloseReplaceContentDialog}
            onDeny={onCloseReplaceContentDialog}
            onConfirm={onConfirmReplaceContentAndTranslate}
        >
            <div style={{ width: 400 }}>
                Are you sure you want to replace existing content?
            </div>
        </ConfirmDialog>
        <ConfirmDialog
            title="Replace All Content"
            confirmLabel="Yes"
            denyLabel="No"
            open={showReplaceAllContentDialog}
            onClose={onCloseReplaceAllContentDialog}
            onDeny={onCloseReplaceAllContentDialog}
            onConfirm={onConfirmReplaceAllContentAndTranslate}
        >
            <div style={{ width: 400 }}>
                Are you sure you want to replace existing content? This will replace all languages except the default.
            </div>
        </ConfirmDialog>
    </>;
};

export default LanguageBarWithTranslateDialog;
